import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { unwrapPage, usePagedList } from "@/lib/paging";
import { ListPager } from "@/components/ui/list-pager";
import { SearchSelect } from "@/components/ui/search-select";
import { useToast } from "@/context/ToastContext";
import { TableSkeleton } from "@/components/ui/page-skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Mail,
  Phone,
  Building,
  UserCheck,
} from "lucide-react";
import type { ContactRecord } from "@/types";
import { useAuth } from "@/context/AuthContext";
import { FilterBar } from "@/components/filters/FilterBar";
import { WhatsAppButton } from "@/components/whatsapp/WhatsAppButton";
import { usePersistentFilters } from "@/lib/usePersistentFilters";
import { dateRangeParams, ownerOptionsFromFacets, toQuery, type OwnerFacet } from "@/lib/filters";
import {
  CalendarDays as FilterDateIcon,
  UserCircle2 as FilterOwnerIcon,
} from "lucide-react";

// Remembered per browser; "all" / "any" mean no filter
const CONTACT_FILTER_DEFAULTS = { owner: "all", created: "any" };

export function ContactsPage() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get("search") || "");
  useEffect(() => setSearch(searchParams.get("search") || ""), [searchParams]);
  const [accountLabel, setAccountLabel] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  // Dialogs
  const [addOpen, setAddOpen] = useState(false);
  const [editContact, setEditContact] = useState<ContactRecord | null>(null);
  const [deleteContact, setDeleteContact] = useState<ContactRecord | null>(null);

  // Form State
  const [form, setForm] = useState({
    account_id: "",
    first_name: "",
    last_name: "",
    job_title: "",
    email: "",
    phone: "",
    mobile: "",
    is_primary: false,
  });

  const { success, error } = useToast();

  const { user } = useAuth();
  const contactFilters = usePersistentFilters("contacts", CONTACT_FILTER_DEFAULTS);
  const [contactOptions, setContactOptions] = useState<{ total: number; owners: OwnerFacet[] }>({ total: 0, owners: [] });

  const list = usePagedList<ContactRecord>({
    deps: [contactFilters.values, search],
    debounceMs: search.trim() ? 350 : 0,
    load: async (page) => {
      const f = contactFilters.values;
      const query = toQuery({
        search: search.trim(),
        owner: f.owner,
        ...dateRangeParams(f.created, "created"),
        page,
        page_size: 30,
      });
      try {
        const [conRes, optRes] = await Promise.all([
          api.get(`/api/Contact/List${query}`),
          api.get("/api/Contact/FilterOptions").catch(() => null),
        ]);
        if (optRes) setContactOptions(unwrapObject(optRes));
        return unwrapPage<ContactRecord>(conRes);
      } catch (err: any) {
        error("Fetch Error", err.message || "Failed to load contacts");
        return { rows: [], total: 0, page, page_size: 30 };
      }
    },
  });
  const contacts = list.rows;
  const loading = list.loading;
  const loadData = list.reload;

  const openAdd = () => {
    setForm({
      account_id: "",
      first_name: "",
      last_name: "",
      job_title: "",
      email: "",
      phone: "",
      mobile: "",
      is_primary: false,
    });
    setAccountLabel("");
    setAddOpen(true);
  };

  const openEdit = (c: ContactRecord) => {
    setAccountLabel(c.account?.name || "");
    setEditContact(c);
    setForm({
      account_id: c.account_id,
      first_name: c.first_name || "",
      last_name: c.last_name || "",
      job_title: c.job_title || "",
      email: c.email || "",
      phone: c.phone || "",
      mobile: c.mobile || "",
      is_primary: !!c.is_primary,
    });
  };

  const handleAddSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.account_id) {
      error("Validation", "Please select an account");
      return;
    }
    setActionLoading(true);
    try {
      await api.post("/api/Contact/Insert", form);
      success("Contact Added", "New contact registered");
      setAddOpen(false);
      loadData();
    } catch (err: any) {
      error("Creation Failed", err.message || "Could not add contact");
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editContact) return;
    setActionLoading(true);
    try {
      await api.put(`/api/Contact/Update/${editContact.id}`, form);
      success("Contact Updated", "Contact details saved");
      setEditContact(null);
      loadData();
    } catch (err: any) {
      error("Update Failed", err.message || "Could not update contact");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!deleteContact) return;
    setActionLoading(true);
    try {
      await api.delete(`/api/Contact/Delete/${deleteContact.id}`);
      success("Contact Deleted", "Contact removed");
      setDeleteContact(null);
      loadData();
    } catch (err: any) {
      error("Delete Failed", err.message || "Could not delete contact");
    } finally {
      setActionLoading(false);
    }
  };

  // The API already applied every filter
  const filtered = contacts;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-foreground flex items-center gap-2">
            <Users className="size-6 text-primary" />
            Contacts Directory
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            Key stakeholders, buyers, procurement agents, and account decision makers
          </p>
        </div>

        <Button onClick={openAdd} className="gap-1.5 bg-primary hover:bg-primary/90">
          <Plus className="size-4" />
          Add Contact
        </Button>
      </div>

      {/* Filters */}
      <FilterBar
        search={{ value: search, onChange: setSearch, placeholder: "Search contacts by name, email, phone, account..." }}
        fields={[
          { name: "owner", label: "Owner", icon: FilterOwnerIcon, type: "select", options: ownerOptionsFromFacets(contactOptions.owners, contactOptions.total, user?.Id), defaultValue: "all" },
          { name: "created", label: "Created", icon: FilterDateIcon, type: "date", dates: "past", defaultValue: "any" },
        ]}
        values={contactFilters.values}
        onChange={(name, value) => contactFilters.set(name as keyof typeof CONTACT_FILTER_DEFAULTS, value)}
        onReset={contactFilters.reset}
        shown={list.total}
        total={list.total}
        noun="contacts"
      />

      {/* Table */}
      {loading && contacts.length === 0 ? (
        <TableSkeleton />
      ) : (
        <div className="rounded-xl border bg-card shadow-sm overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50 text-xs">
                <TableHead>Contact Name</TableHead>
                <TableHead>Account</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Phone / Mobile</TableHead>
                <TableHead>Role Type</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-xs text-muted-foreground">
                    No contacts found
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((con) => (
                  <TableRow key={con.id} className="text-xs">
                    <TableCell className="font-semibold text-foreground">
                      <div className="flex items-center gap-2">
                        <div className="size-7 rounded-full bg-primary/10 text-primary flex items-center justify-center font-bold text-xs">
                          {con.first_name[0]?.toUpperCase()}
                        </div>
                        <div>
                          <Link
                            to={`/contacts/${con.id}`}
                            className="font-semibold text-foreground hover:text-primary hover:underline"
                          >
                            {con.first_name} {con.last_name || ""}
                          </Link>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      <div className="flex items-center gap-1">
                        <Building className="size-3 text-muted-foreground/70" />
                        {con.account ? (
                          <Link
                            to={`/accounts/${con.account_id || con.account.id}`}
                            className="hover:text-primary hover:underline text-foreground/80"
                          >
                            {con.account.name}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">{con.job_title || "—"}</TableCell>
                    <TableCell>
                      {con.email ? (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Mail className="size-3 text-muted-foreground/70" />
                          {con.email}
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {con.phone || con.mobile ? (
                        <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                          <Phone className="size-3 text-muted-foreground/70" />
                          {con.phone || con.mobile}
                          <WhatsAppButton
                            phone={con.mobile || con.phone}
                            name={`${con.first_name} ${con.last_name || ""}`.trim()}
                            contactId={con.id}
                          />
                        </div>
                      ) : "—"}
                    </TableCell>
                    <TableCell>
                      {con.is_primary ? (
                        <Badge className="bg-blue-500/10 text-blue-600 border-blue-500/20 text-[10px]">
                          Primary
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-[11px]">Secondary</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right space-x-1">
                      <Button variant="ghost" size="icon" title="Edit" onClick={() => openEdit(con)} className="size-8">
                        <Edit className="size-3.5" />
                      </Button>
                      <Button variant="ghost" size="icon" title="Delete" onClick={() => setDeleteContact(con)} className="size-8 text-destructive">
                        <Trash2 className="size-3.5" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <ListPager page={list.page} pageSize={list.pageSize} total={list.total} onPage={list.setPage} disabled={loading} />
        </div>
      )}

      {/* Add / Edit Dialog */}
      <Dialog open={addOpen || !!editContact} onOpenChange={(open) => !open && (setAddOpen(false), setEditContact(null))}>
        <DialogContent className="sm:max-w-xl md:max-w-2xl">
          <form onSubmit={editContact ? handleEditSubmit : handleAddSubmit}>
            <DialogHeader>
              <DialogTitle>{editContact ? "Edit Contact" : "Add Contact"}</DialogTitle>
              <DialogDescription>
                Assign this contact to an account organization and configure details.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4 text-xs sm:text-sm">
              <div className="space-y-1.5">
                <Label htmlFor="contact_account" className="text-xs sm:text-sm font-semibold">Organization Account *</Label>
                <SearchSelect
                  value={form.account_id}
                  selectedLabel={accountLabel}
                  endpoint="/api/Account/List"
                  placeholder="Search accounts"
                  getLabel={(a) => a.name}
                  onChange={(id, row) => {
                    setAccountLabel(row?.name || "");
                    setForm((p) => ({ ...p, account_id: id }));
                  }}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="con_first_name" className="text-xs sm:text-sm font-semibold">First Name *</Label>
                  <Input
                    id="con_first_name"
                    value={form.first_name}
                    onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))}
                    required
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="con_last_name" className="text-xs sm:text-sm font-semibold">Last Name</Label>
                  <Input
                    id="con_last_name"
                    value={form.last_name}
                    onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))}
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="con_job" className="text-xs sm:text-sm font-semibold">Job Title</Label>
                <Input
                  id="con_job"
                  placeholder="e.g. Procurement Lead, Director"
                  value={form.job_title}
                  onChange={(e) => setForm((p) => ({ ...p, job_title: e.target.value }))}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div className="space-y-1.5">
                  <Label htmlFor="con_email" className="text-xs sm:text-sm font-semibold">Work Email</Label>
                  <Input
                    id="con_email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="con_phone" className="text-xs sm:text-sm font-semibold">Direct Phone</Label>
                  <Input
                    id="con_phone"
                    value={form.phone}
                    onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))}
                  />
                </div>
              </div>

              <div className="flex items-center gap-2.5 pt-2">
                <input
                  type="checkbox"
                  id="is_primary_checkbox"
                  checked={form.is_primary}
                  onChange={(e) => setForm((p) => ({ ...p, is_primary: e.target.checked }))}
                  className="rounded border-input text-primary focus:ring-primary size-4"
                />
                <Label htmlFor="is_primary_checkbox" className="cursor-pointer font-semibold text-xs sm:text-sm">
                  Designate as Primary Account Contact
                </Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => (setAddOpen(false), setEditContact(null))}>
                Cancel
              </Button>
              <Button type="submit" disabled={actionLoading}>
                {actionLoading ? "Saving..." : editContact ? "Save Changes" : "Create Contact"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteContact} onOpenChange={(open) => !open && setDeleteContact(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Confirm Delete Contact</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{deleteContact?.first_name} {deleteContact?.last_name}</strong>?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDeleteContact(null)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDeleteSubmit} disabled={actionLoading}>
              {actionLoading ? "Deleting..." : "Delete Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
